import React, { useState } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Sidebar, WorkflowType } from './components/navigation/Sidebar';
import { PolicyAnalysisWorkflow } from './components/workflows/PolicyAnalysisWorkflow';
import { ComingSoonWorkflow } from './components/workflows/ComingSoonWorkflow';
import { SettingsWorkflow } from './components/workflows/SettingsWorkflow';
import { UpdateNotification } from './components/ui/UpdateNotification';
import { useSession } from './hooks/useSession';
import { usePWAUpdate } from './hooks/usePWAUpdate';
import { ThemeProvider } from './contexts/ThemeContext';
import { DemoProvider } from './contexts/DemoContext';
import { FileSearch, Edit, User, Bell, Settings } from 'lucide-react';
import './index.css';

function App() {
  const { session, sendMessage, clearSession, cancelAnalysis, loadDemoReport } = useSession();
  const { updateAvailable, updateApp, dismissUpdate } = usePWAUpdate();
  const [activeWorkflow, setActiveWorkflow] = useState<WorkflowType>('policy-analysis');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const renderWorkflow = () => {
    switch (activeWorkflow) {
      case 'policy-analysis':
        return (
          <PolicyAnalysisWorkflow 
            session={session}
            sendMessage={sendMessage}
            loadDemoReport={loadDemoReport}
            onCancelAnalysis={cancelAnalysis}
          />
        );
      case 'bill-tracking':
        return (
          <ComingSoonWorkflow
            title="Bill Tracking"
            description="Monitor and track the progress of legislation through Congress with real-time updates and custom alerts."
            icon={FileSearch}
            features={[
              "Real-time bill status updates",
              "Custom tracking lists and portfolios",
              "Committee hearing schedules",
              "Amendment tracking and analysis",
              "Stakeholder position mapping"
            ]}
          />
        );
      case 'redlines':
        return (
          <ComingSoonWorkflow
            title="Redlines"
            description="Compare different versions of legislation and analyze changes with AI-powered insights."
            icon={Edit}
            features={[
              "Side-by-side document comparison",
              "AI-powered change analysis",
              "Impact assessment of amendments",
              "Stakeholder reaction tracking",
              "Historical version management"
            ]}
          />
        );
      case 'legislator-profiles':
        return (
          <ComingSoonWorkflow
            title="Legislator Profiles"
            description="Comprehensive profiles of legislators with voting records, policy positions, and influence analysis."
            icon={User}
            features={[
              "Voting record analysis",
              "Policy position tracking",
              "Committee influence mapping",
              "Fundraising and donor analysis",
              "Sponsorship and co-sponsorship patterns"
            ]}
          />
        );
      case 'alerts':
        return (
          <ComingSoonWorkflow
            title="Alerts & Notifications"
            description="Stay informed with custom alerts and notifications for legislative activity that matters to you."
            icon={Bell}
            features={[
              "Custom alert criteria",
              "Multi-channel notifications",
              "Smart digest summaries",
              "Priority-based filtering",
              "Team collaboration alerts"
            ]}
          />
        );
      case 'settings':
        return <SettingsWorkflow />;
      default:
        return null;
    }
  };

  return (
    <ThemeProvider>
      <DemoProvider>
        <div className="h-screen flex flex-col theme-bg">
          <Header 
            onClearSession={clearSession}
            sessionTopic={session.sessionTopic}
            congressSessions={session.congressSessions}
          />
          
          <div className="flex-1 min-h-0 flex overflow-hidden">
            <Sidebar
              activeWorkflow={activeWorkflow}
              onWorkflowChange={setActiveWorkflow}
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
            
            <div className="flex-1 min-h-0 overflow-hidden">
              {renderWorkflow()}
            </div>
          </div>
          
          {/* Footer - only visible on mobile */}
          <Footer />
          
          <UpdateNotification 
            isVisible={updateAvailable}
            onUpdate={updateApp}
            onDismiss={dismissUpdate}
          />
        </div>
      </DemoProvider>
    </ThemeProvider>
  );
}

export default App;